import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext'; // ✅ Added import
import {
  LayoutDashboard,
  Package,
  FileText,
  CreditCard,
  User,
  Users,
  Phone,
  Shield,
  Settings,
  MessageSquare,
  HelpCircle,
  LogOut,
  Search,
  ChevronDown,
  Menu,
  X,
  MessageCircle,
  Sun,
  Moon,
  Layers,
  ShieldAlert,
  Landmark,
  QrCode,
  Truck
} from 'lucide-react';
import { UserProfileDto } from '../types/dtos';
import apiClient from '../api/client';
import NotificationDropdown from './NotificationDropdown';

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

export default function Sidebar({ isOpen, setIsOpen, theme, toggleTheme }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<UserProfileDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Change Password Modal State
  const [showChangePassModal, setShowChangePassModal] = useState(false);
  const [passData, setPassData] = useState({ currentPassword: '', newPassword: '' });
  const [passLoading, setPassLoading] = useState(false);
  const [passError, setPassError] = useState<string | null>(null);
  const [passSuccess, setPassSuccess] = useState<string | null>(null);

  // Fetch user on mount
  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get<UserProfileDto>('/admin/profile');
        setUser(res.data);
      } catch (err) {
        console.error('Failed to load user profile:', err);
        // Optionally handle auth failure here
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  const isSuperAdmin = user?.company === "Xtrem Group";

  const allMenuItems = [
    { id: 'dashboard', label: 'My Dashboard', icon: LayoutDashboard, path: '/' },
    { id: 'core-policies', label: 'Policy Administration', icon: Shield, path: '/core/policies' },
    { id: 'core-fleets', label: 'Commercial Fleets', icon: Truck, path: '/core/fleets' },
    { id: 'core-claims', label: 'Claims Engine', icon: ShieldAlert, path: '/core/claims' },
    { id: 'core-intermediaries', label: 'Brokers & Bordereaux', icon: Users, path: '/core/intermediaries' },
    { id: 'core-reinsurance', label: 'Reinsurance Treaties', icon: Layers, path: '/core/reinsurance' },
    { id: 'core-regulatory', label: 'RBM Solvency Returns', icon: Landmark, path: '/core/regulatory' },
    { id: 'police-verification', label: 'Police QR Verification', icon: QrCode, path: '/verify-cover-note' },
    { id: 'users', label: 'User Management', icon: User, path: '/users', superAdminOnly: true },
    { id: 'roles', label: 'Role Management', icon: Shield, path: '/roles', superAdminOnly: true },
    { id: 'products', label: 'Product Catalog', icon: Package, path: '/products' },
    { id: 'payments', label: 'Payments', icon: CreditCard, path: '/payments' },
    { id: 'applications', label: 'Policy Applications', icon: FileText, path: '/applications' },
    { id: 'customers', label: 'Customers', icon: Users, path: '/admin/customers' },
    { id: 'support', label: 'Support Requests', icon: Phone, path: '/admin/support' },
  ];

  const allSupportItems = [
    { id: 'user-feedback', label: 'User Feedback', icon: MessageSquare, path: '/admin/feedback' },
    { id: 'whatsapp-chats', label: 'WhatsApp Chats', icon: MessageCircle, path: '/whatsapp-chats' },
    { id: 'config', label: 'Configuration', icon: Settings, path: '/config', superAdminOnly: true },
    { id: 'help-center', label: 'Help Center', icon: HelpCircle, path: '/admin/help-center' },
  ];

  const menuItems = allMenuItems.filter(item => !item.superAdminOnly || isSuperAdmin);
  const supportItems = allSupportItems.filter(item => !item.superAdminOnly || isSuperAdmin);

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/'; // Only match exact root
    }
    return location.pathname.startsWith(path); // e.g., '/users' matches '/users', '/users/new', etc.
  };

  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPassLoading(true);
    setPassError(null);
    setPassSuccess(null);

    try {
      await apiClient.post('/admin/auth/change-password', passData);
      setPassSuccess('Password changed successfully.');
      setPassData({ currentPassword: '', newPassword: '' });
      setTimeout(() => {
        setShowChangePassModal(false);
        setPassSuccess(null);
      }, 2000);
    } catch (err: any) {
      setPassError(err.response?.data?.message || 'Failed to change password.');
    } finally {
      setPassLoading(false);
    }
  };

  return (
    <>

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        ></div>
      )}

      <aside
        className={`bg-brand-primary dark:bg-slate-900 text-white w-64 min-w-64 border-r border-white/5 dark:border-slate-800/80 flex flex-col fixed lg:sticky lg:top-0 lg:h-screen inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Close button for mobile */}
        <button
          onClick={() => setIsOpen(false)}
          className="lg:hidden absolute top-4 right-4 p-2 text-white/50 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>

        {/* Logo & Notifications */}
        <div className="p-5 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Xtrem Logo" className="h-9 w-auto hover:rotate-12 transition-transform" />
            <span className="font-black text-lg tracking-tight">Xtrem Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-1.5 hover:bg-white/5 rounded-lg text-white/50 hover:text-white transition-colors"
              aria-label="Toggle Theme"
            >
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} className="text-amber-400" />}
            </button>
            <NotificationDropdown direction="right" isDark={true} />
          </div>
        </div>

        {/* User Profile */}
        <div className="p-5 border-b border-white/5">
          <div className="flex items-center gap-3">
            {loading ? (
              <div className="w-10 h-10 rounded-xl bg-white/10 animate-pulse"></div>
            ) : (
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white font-black border border-white/15 shadow-inner">
                {user ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}` : '??'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              {loading ? (
                <div className="h-4 bg-white/10 rounded w-3/4 animate-pulse"></div>
              ) : user ? (
                <>
                  <button 
                    onClick={() => setShowChangePassModal(true)}
                    className="flex items-center gap-1.5 group"
                  >
                    <span className="text-sm font-bold truncate group-hover:text-white/80 transition-colors">
                      {user.firstName} {user.lastName}
                    </span>
                    <Settings size={13} className="text-white/50 flex-shrink-0 group-hover:text-white transition-colors" />
                  </button>
                  <div className="flex flex-col gap-1 mt-1">
                    <span className={`w-fit inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                      isSuperAdmin 
                        ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' 
                        : 'bg-white/10 text-white/80 border border-white/15'
                    }`}>
                      {isSuperAdmin ? 'Super Admin' : 'Company Admin'}
                    </span>
                    <span className="text-[10px] text-white/40 truncate font-semibold">{user.company}</span>
                  </div>
                </>
              ) : (
                <div className="text-sm text-white/50 truncate">
                  Guest
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="p-5 border-b border-white/5">
          <div className="relative">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              placeholder="Search features..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-xs sm:text-sm focus:outline-none focus:border-white/20 focus:ring-4 focus:ring-white/5 transition-all text-white placeholder-white/40 font-medium"
            />
          </div>
        </div>

        {/* Main Menu */}
        <nav className="flex-1 p-5 overflow-y-auto space-y-6">
          <div>
            <div className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-3 px-3">MAIN MENU</div>
            <ul className="space-y-1.5">
              {menuItems
                .filter(item => item.label.toLowerCase().includes(searchTerm.toLowerCase()))
                .map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.id}>
                      <Link
                        to={item.path}
                        className={`w-full flex items-center gap-3 px-3.5 py-3 text-xs sm:text-sm font-bold rounded-xl border border-transparent transition-all duration-200 ${
                          isActive(item.path)
                            ? 'bg-brand-secondary text-white border-white/10 shadow-[0_4px_12px_rgba(0,0,0,0.1)]'
                            : 'text-white/70 hover:bg-white/5 hover:text-white'
                        }`}
                        aria-current={isActive(item.path) ? 'page' : undefined}
                      >
                        <Icon size={18} className={isActive(item.path) ? 'text-white' : 'text-white/50'} />
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              {menuItems.filter(item => item.label.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && searchTerm && (
                <li className="px-3 py-2 text-xs text-white/40 italic">No menu items found</li>
              )}
            </ul>
          </div>

          {/* Support */}
          <div className="pt-4 border-t border-white/5">
            <div className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-3 px-3">SUPPORT</div>
            <ul className="space-y-1.5">
              {supportItems
                .filter(item => item.label.toLowerCase().includes(searchTerm.toLowerCase()))
                .map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.id}>
                      <Link
                        to={item.path}
                        className={`w-full flex items-center gap-3 px-3.5 py-3 text-xs sm:text-sm font-bold rounded-xl border border-transparent transition-all duration-200 ${
                          isActive(item.path)
                            ? 'bg-brand-secondary text-white border-white/10 shadow-[0_4px_12px_rgba(0,0,0,0.1)]'
                            : 'text-white/70 hover:bg-white/5 hover:text-white'
                        }`}
                        aria-current={isActive(item.path) ? 'page' : undefined}
                      >
                        <Icon size={18} className={isActive(item.path) ? 'text-white' : 'text-white/50'} />
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              {/* Logout */}
              <li>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3.5 py-3 text-xs sm:text-sm font-bold text-red-300 hover:bg-red-500/10 hover:text-red-200 rounded-xl transition-all"
                  aria-label="Log out of Xtrem Admin Portal"
                >
                  <LogOut size={18} className="text-red-400" />
                  <span>Logout</span>
                </button>
              </li>
            </ul>
          </div>
        </nav>
      </aside>

      {/* Change Password Modal */}
      {showChangePassModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-brand-primary dark:bg-slate-900 border border-white/10 dark:border-slate-800 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200 text-white">
            <div className="bg-gradient-to-r from-brand-primary to-brand-secondary dark:from-slate-900 dark:to-slate-800 px-6 py-4.5 flex items-center justify-between text-white border-b border-white/5 dark:border-slate-800/80">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Shield size={20} className="text-brand-accent" /> Update Password
              </h3>
              <button 
                onClick={() => setShowChangePassModal(false)}
                className="text-white/60 hover:text-white p-1 hover:bg-white/5 rounded-full transition-all"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleChangePassword} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-white/50 uppercase tracking-widest mb-2">Current Password</label>
                <input
                  type="password"
                  required
                  className="w-full px-4 py-2.5 bg-white/5 dark:bg-slate-950/45 border border-white/10 dark:border-slate-800 rounded-xl text-white text-sm focus:border-white/20 focus:ring-4 focus:ring-white/5 transition-all outline-none font-medium"
                  value={passData.currentPassword}
                  onChange={e => setPassData({ ...passData, currentPassword: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-white/50 uppercase tracking-widest mb-2">New Password</label>
                <input
                  type="password"
                  required
                  className="w-full px-4 py-2.5 bg-white/5 dark:bg-slate-950/45 border border-white/10 dark:border-slate-800 rounded-xl text-white text-sm focus:border-white/20 focus:ring-4 focus:ring-white/5 transition-all outline-none font-mono"
                  value={passData.newPassword}
                  onChange={e => setPassData({ ...passData, newPassword: e.target.value })}
                />
              </div>

              {passError && <p className="px-4 py-2 bg-red-950/40 border border-red-500/20 text-red-200 rounded-lg text-xs font-medium">{passError}</p>}
              {passSuccess && <p className="px-4 py-2 bg-green-950/40 border border-green-500/20 text-green-200 rounded-lg text-xs font-medium">{passSuccess}</p>}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowChangePassModal(false)}
                  className="flex-1 px-4 py-2.5 border border-white/10 rounded-xl text-sm font-semibold text-white/50 hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={passLoading}
                  className="flex-2 px-6 py-2.5 bg-brand-secondary hover:opacity-95 text-white rounded-xl text-sm font-bold shadow-lg disabled:opacity-50 transition-all"
                >
                  {passLoading ? 'Updating...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}